/**
 * TemplateQualityControl — reporte de calidad de la versión que se edita, en
 * la cabecera de todos los editores (OT-0006).
 *
 * Resume los hallazgos por severidad y abre el detalle en un diálogo. La
 * query se invalida al guardar (`useDefinitionAutosave`) y al publicar.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'

import {
  getVersionQuality,
  versionQualityQueryKey,
  type Template,
  type TemplateVersion,
} from '../../../api/templates'
import { retryUnlessForbiddenTier } from '../../../api/queryRetry'
import { useAccessToken } from '../../../auth/useAccessToken'
import { Dialog } from '../../Dialog'
import { QualityFindingList, QualitySummary } from './QualityFindingList'

export function TemplateQualityControl({
  template,
  version,
}: {
  template: Template
  version: TemplateVersion
}) {
  const { t } = useTranslation(['templates', 'common'])
  const { accessToken } = useAccessToken()
  const [open, setOpen] = useState(false)
  const query = useQuery({
    queryKey: versionQualityQueryKey(template.id, version.id),
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getVersionQuality(accessToken, template.id, version.id)
    },
    enabled: !!accessToken,
    retry: retryUnlessForbiddenTier,
  })

  if (query.isError) {
    return <span className="text-xs text-ink-subtle">{t('quality.load_error')}</span>
  }
  const report = query.data
  if (!report) return null
  if (report.findings.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-official">
        <ShieldCheck className="w-3.5 h-3.5" />
        {t('quality.header_clean')}
      </span>
    )
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-line px-2 py-1 text-xs hover:border-line-strong"
      >
        <ShieldCheck className="w-3.5 h-3.5 text-ink-subtle" />
        <span className="text-ink-muted">{t('quality.header_button')}:</span>
        <QualitySummary findings={report.findings} />
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('quality.dialog_title')}
        footer={
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md"
          >
            {t('common:actions.close')}
          </button>
        }
      >
        <p className="text-xs text-ink-muted leading-relaxed mb-3">{t('quality.intro')}</p>
        <QualityFindingList findings={report.findings} />
      </Dialog>
    </>
  )
}
